import { useQuery } from '@apollo/client';
import {
  DriftCalculationMethod,
  DriftRecalculationOutcomes,
  DriftRecalculationStates,
  FeatureFlagTypes,
} from '@generated/graphql';
import { useToast } from 'hephaestus/web';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationFeatureFlag } from '../../../hooks/useOrganizationFeatureFlag';
import { FETCH_DRIFT_STATE } from './useOrgDriftSettings.query';

// Drift-specific query kept separate from the page's FETCH_ORGANIZATION so
// its no-cache + 5s polling doesn't pollute the settings cache or re-render
// the whole page each tick. Gateway merges the fields either way.

const DRIFT_RECALC_STALE_MS = 30 * 60 * 1000;
const DRIFT_POLL_INTERVAL_MS = 5000;
const DRIFT_EXPECT_RECALC_TIMEOUT_MS = 30000;
export const DRIFT_MIN = 5;
export const DRIFT_MAX = 20;

/**
 * Blank is valid — it means inherit. Anything else must be a whole number
 * inside the supported band. Standalone rather than folded into the org
 * hook's isDriftInvalid, because the model portfolio create and edit flows
 * validate the same field with no org state in scope.
 */
export const isDriftToleranceInvalid = (
  value: number | null | undefined,
): boolean => {
  if (value == null) return false;
  return !Number.isInteger(value) || value < DRIFT_MIN || value > DRIFT_MAX;
};

/** An org that has never set a method resolves to Relative on the backend. */
export const DEFAULT_DRIFT_CALCULATION_METHOD = DriftCalculationMethod.Relative;

export const useOrgDriftSettings = (organizationId: string | undefined) => {
  // The singular hook reads the effective flag set off UserContext. The plural
  // one drives a permission-gated query, which hid the whole drift block from
  // users without read:feature_flags.
  const isToleranceFlagEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.DriftToleranceEnabled,
  );
  const isMethodFlagEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.DriftCalculationMethodEnabled,
  );
  const { enqueueToast } = useToast();
  const { t } = useTranslation(['settings']);
  // Either flag surfaces the drift block; only flag B surfaces the method.
  const isDriftFeatureEnabled = isToleranceFlagEnabled || isMethodFlagEnabled;
  const isMethodFieldVisible = isMethodFlagEnabled;

  const { data, startPolling, stopPolling, refetch } = useQuery(
    FETCH_DRIFT_STATE,
    {
      variables: { organizationId },
      fetchPolicy: 'no-cache',
      skip: !isDriftFeatureEnabled || !organizationId,
    },
  );
  const fetched = data?.fetchOrganization?.organization;

  const [initialDriftPercent, setInitialDriftPercent] = useState<
    number | null | undefined
  >(undefined);
  const [currentDriftPercent, setCurrentDriftPercent] = useState<
    number | null | undefined
  >(undefined);
  const [initialMethod, setInitialMethod] = useState<
    DriftCalculationMethod | undefined
  >(undefined);
  const [currentMethod, setCurrentMethod] = useState<
    DriftCalculationMethod | undefined
  >(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expectingRecalc, setExpectingRecalc] = useState(false);
  // The marker as it stood before this client's own save, and whether we are
  // still waiting on it. Compared rather than watched: a run can open and
  // close between two polls, and a change with nothing to screen never opens
  // one at all, so waiting to SEE recalculating misses both.
  // `undefined` means we had no document to read a baseline from: the form is
  // seeded from a different query, so a save can beat this one's first
  // response. Without the distinction the leftover marker from some earlier
  // change reads as this save's answer the moment that response lands.
  const settledAtBeforeSaveRef = useRef<string | null | undefined>(undefined);
  const [awaitingSettle, setAwaitingSettle] = useState(false);

  // What the document says, regardless of age. pendingDriftRecalc is the
  // difference between QUEUED and IDLE: a run that exits dirty is released as
  // IDLE + pending with the sweep about to re-dispatch, and reading state
  // alone called that finished — the form unlocked and the completion toast
  // fired over a run that had not happened yet.
  const documentIsRecalculating =
    fetched?.driftRecalculationState ===
      DriftRecalculationStates.Recalculating ||
    fetched?.pendingDriftRecalc === true;

  // Stale locks (>30 min) → IDLE, so a crashed worker doesn't lock the UI.
  // A queued run has no startedAt — the release unsets it — so the stale-age
  // check cannot apply to it, and queued is unconditionally live.
  const serverIsRecalculating =
    isDriftFeatureEnabled &&
    (fetched?.pendingDriftRecalc === true ||
      (documentIsRecalculating &&
        !!fetched?.driftRecalculationStartedAt &&
        Date.now() - new Date(fetched.driftRecalculationStartedAt).getTime() <
          DRIFT_RECALC_STALE_MS));
  const settledAt = fetched?.driftRecalculationSettledAt ?? null;
  const settledSinceSave =
    awaitingSettle &&
    settledAtBeforeSaveRef.current !== undefined &&
    settledAt !== settledAtBeforeSaveRef.current;
  const isRecalculating =
    serverIsRecalculating || (expectingRecalc && !settledSinceSave);

  useEffect(() => {
    if (fetched && initialDriftPercent === undefined) {
      const seed = fetched.defaultDriftTolerancePercent ?? null;
      setInitialDriftPercent(seed);
      setCurrentDriftPercent(seed);
      // Seeded with the effective value, not null: the select displays Relative
      // for an unset org, so seeding null would make picking Absolute and then
      // Relative again look like a no-op while still queueing an org-wide
      // recalculation.
      const seedMethod =
        fetched.defaultDriftCalculationMethod ??
        DEFAULT_DRIFT_CALCULATION_METHOD;
      setInitialMethod(seedMethod);
      setCurrentMethod(seedMethod);
    }
  }, [fetched, initialDriftPercent]);

  useEffect(() => {
    if (isDriftFeatureEnabled && isRecalculating) {
      startPolling(DRIFT_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
  }, [isDriftFeatureEnabled, isRecalculating, startPolling, stopPolling]);

  useEffect(() => {
    if (expectingRecalc && serverIsRecalculating) {
      setExpectingRecalc(false);
    }
  }, [expectingRecalc, serverIsRecalculating]);

  // If a recalc starts while the confirm dialog is open, close it — the
  // locked field + banner become the source of truth.
  useEffect(() => {
    if (confirmOpen && isRecalculating) setConfirmOpen(false);
  }, [confirmOpen, isRecalculating]);

  // Safety net: a missed backend dispatch shouldn't permanently lock the UI.
  //
  // Gives up the settle wait too. The two are armed together and polling stops
  // with them, so leaving the wait armed freezes the last marker we saw: the
  // next save would compare against that stale value and read the previous
  // change's marker, arriving late, as an answer about itself.
  useEffect(() => {
    if (!expectingRecalc) return;
    const id = setTimeout(() => {
      setExpectingRecalc(false);
      setAwaitingSettle(false);
    }, DRIFT_EXPECT_RECALC_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [expectingRecalc]);

  // The one completion signal: this client's own change, settled by the
  // backend. An idle document is not one — a run can go idle having exited
  // dirty, and drain later as nothing to recalculate, which is the case the
  // marker exists to keep quiet. Document state locks the form; only the
  // marker speaks.
  useEffect(() => {
    if (!settledSinceSave) return;
    setAwaitingSettle(false);
    setExpectingRecalc(false);
    if (
      fetched?.driftRecalculationSettledOutcome ===
      DriftRecalculationOutcomes.Recalculated
    ) {
      enqueueToast(t('trades.driftRecalculationComplete'), {
        variant: 'success',
      });
    }
  }, [
    settledSinceSave,
    fetched?.driftRecalculationSettledOutcome,
    enqueueToast,
    t,
  ]);

  const driftChanged =
    isDriftFeatureEnabled && currentDriftPercent !== initialDriftPercent;
  const methodChanged = isMethodFieldVisible && currentMethod !== initialMethod;
  // A method change triggers the same org-wide recalculation as a tolerance
  // change, so both gate the dialog, the lock and the banner.
  const settingsChanged = driftChanged || methodChanged;

  // Only the user's own edit can be invalid. A pre-existing server value
  // (empty or out-of-range from before range enforcement) must not block
  // saving unrelated settings on load.
  const isDriftInvalid =
    isDriftFeatureEnabled &&
    driftChanged &&
    (currentDriftPercent == null ||
      Number.isNaN(currentDriftPercent) ||
      currentDriftPercent < DRIFT_MIN ||
      currentDriftPercent > DRIFT_MAX);

  const onBeforeMutation = () => {
    if (!settingsChanged) return;
    settledAtBeforeSaveRef.current = fetched ? settledAt : undefined;
    setAwaitingSettle(true);
    setExpectingRecalc(true);
  };

  // Runs only on OUR successful mutation, so currentDriftPercent is now the
  // persisted value — reset the baseline from it optimistically so a refetch
  // failure can't strand driftChanged=true. (The toast effect must NOT do
  // this: it fires for any recalc completion, including unrelated ones.)
  const onMutationCompleted = () => {
    if (currentDriftPercent !== undefined) {
      setInitialDriftPercent(currentDriftPercent);
    }
    if (currentMethod !== undefined) {
      setInitialMethod(currentMethod);
    }
    refetch().catch(() => {});
  };

  const onMutationFailed = () => {
    if (!settingsChanged) return;
    setAwaitingSettle(false);
    setExpectingRecalc(false);
  };

  // Only send drift when the user changed it — re-sending an unchanged
  // (possibly out-of-range legacy) value would fail backend validation and
  // block the whole save.
  const mutationInputDelta = {
    ...(isDriftFeatureEnabled && driftChanged
      ? { defaultDriftTolerancePercent: currentDriftPercent }
      : {}),
    ...(methodChanged && currentMethod
      ? { defaultDriftCalculationMethod: currentMethod }
      : {}),
  };

  return {
    isDriftFeatureEnabled,
    isMethodFieldVisible,
    isRecalculating,
    currentDriftPercent,
    setCurrentDriftPercent,
    currentMethod,
    setCurrentMethod,
    driftChanged,
    methodChanged,
    settingsChanged,
    isDriftInvalid,
    confirmOpen,
    setConfirmOpen,
    onBeforeMutation,
    onMutationCompleted,
    onMutationFailed,
    mutationInputDelta,
  };
};
